<template>
  <div class="book d-block d-lg-flex" :class="{ 'book--confirmation': step === 5 }" id="step-wrapper">
    <nav-sidebar :step="step"/>
    <div class="content align-self-start">
      <step-one v-if="step === 1" @valid="handleStepValid" @invalid="handleStepInvalid"/>
      <step-two v-if="step === 2" @valid="handleStepValid" @invalid="handleStepInvalid"/>
      <step-three v-if="step === 3" @prev="prev" @valid="handleStepValid" @invalid="handleStepInvalid" />
      <step-four v-if="step === 4" @prev="prev" @valid="handleStepValid" @invalid="handleStepInvalid" />
      <confirmation v-if="step === 5"></confirmation>
    </div>
    <totals-sidebar v-if="step !== 5" @next="next" :step="step" :disableNext="!stepValid" :isLoading="waitingForResponse" :isPricingLoading="pricing.isLoading" @prev="prev"/>
    <loader :show="waitingForResponse"></loader>
  </div>
</template>

<script>
import StepOne from '@/components/StepOne'
import StepTwo from '@/components/StepTwo'
import StepThree from '@/components/StepThree'
import StepFour from '@/components/StepFour'
import Confirmation from '@/components/Confirmation'
import NavSidebar from '@/components/NavSidebar'
import TotalsSidebar from '@/components/TotalsSidebar'
import Loader from '@/components/Loader'
import { mapGetters, mapMutations } from 'vuex'
import { mapFields } from 'vuex-map-fields'
import axios from 'axios'
import { getPricingSessionId, requestPricingQuote, validatePricingCheckout } from '@/services/pricing'
import "../assets/polyfills"

export default {
  name: 'Book',
  computed: {
    ...mapFields(['payment.totalPreCoupon', 'payment.confirmedAmount']),
    ...mapGetters(['prices', 'payment', 'booking', 'bookingHome', 'contact', 'cart', 'puroadLevel', 'pricing']),
    pricingInputFingerprint() {
      return JSON.stringify({
        cart: this.cart,
        booking: this.booking,
        bookingHome: this.bookingHome,
        contact: this.contact,
        payment: {
          coupon: this.payment.coupon,
          couponValue: this.payment.couponValue,
          couponType: this.payment.couponType,
          couponExpirydate: this.payment.couponExpirydate,
          couponIncludeUdrejse: this.payment.couponIncludeUdrejse,
          couponIncludeHjemrejse: this.payment.couponIncludeHjemrejse
        },
        puroadLevel: this.puroadLevel
      })
    },
    isFullCouponCheckout() {
      const total = Number(this.prices && this.prices.total ? this.prices.total : 0);
      const totalPreCoupon = Number(this.payment && this.payment.totalPreCoupon ? this.payment.totalPreCoupon : 0);
      const couponReduction = Number(this.payment && this.payment.couponValueCU ? this.payment.couponValueCU : 0);

      return total <= 0
        && totalPreCoupon > 0
        && couponReduction >= totalPreCoupon
        && !!this.payment.coupon
        && !!this.payment.couponType
        && !this.payment.couponError;
    }
  },
  data() {
    return {
      step: 1,
      image: '../assets/images/bg-front.jpg',
      name: 'Færdiggør din bestilling',
      description: 'Udfyld venligst herunder',
      currency: 'DKK',
      locale: 'da',
      stepValid: false,
      waitingForResponse: false,
      bookingForSend: {},
      referral: document.cookie.split(';') || [],
      orderRequestId: null,
      pricingRequestCounter: 0,
      pricingDebounceTimer: null,
      pricingSessionId: ''
    }
  },
  components: {
    StepOne,
    StepTwo,
    StepThree,
    StepFour,
    Confirmation,
    TotalsSidebar,
    NavSidebar,
    Loader
  },

  watch:{
    $route (to){
      if (typeof to.query.post_n !== 'undefined') {
        this.contact.address.zip = to.query.post_n;
      }

      if (typeof to.query.amt_people !== 'undefined' && this.step === 1) {
        this.setPeople(to.query.amt_people);
      }

      // Enhanced test mode activation
      if (typeof to.query.vzoman !== 'undefined' || this.shouldActivateTestMode()) {
        this.payment.type = 'Mobilepay'
      }

    },
    step() {
      this.addHashToStep()
    },
    pricingInputFingerprint() {
      if (this.step < 5) {
        this.queuePricingQuote()
      }
    }
  },
  mounted() {
    this.payment.confirmedAmount = null;
    this.pricingSessionId = getPricingSessionId();

    // Add global test helper for debugging
    window.valizoTestMode = {
      enable: () => {
        localStorage.setItem('valizo_test_mode', 'true');
        this.payment.type = 'Mobilepay';
        return this.payment.type;
      },
      disable: () => {
        localStorage.removeItem('valizo_test_mode');
        return true;
      },
      check: () => {
        return this.shouldActivateTestMode();
      }
    };

    if (Array.isArray(this.referral) && this.referral.length) {
      this.referral = this.referral.find(c => c.includes('ref=')) || '';
      if (this.referral) {
        this.referral = this.referral.split('=')[1];
      }
    } else {
      this.referral = '';
    }

    let query = this.$route.query;
    
    // FALLBACK: Parse window.location.search if route query is empty (happens when params are before hash)
    if ((!query.quickpay || !query.token) && window.location.search.includes('quickpay')) {
      const urlParams = new URLSearchParams(window.location.search);
      const manualQuery = Object.fromEntries(urlParams.entries());
      if (manualQuery.quickpay && manualQuery.token) {
        query = { ...query, ...manualQuery };
      }
    }
    
    // Handle return from Quickpay redirect
    if (query.quickpay && query.token) {
      this.step = 5; // Set step to 5 immediately BEFORE calling handler
      this.handleQuickpayReturn(query.quickpay, query.token);
      return; // Don't call addHashToStep() - watcher will handle it
    }

    if ((Object.entries(query).length === 0 && query.constructor === Object) === false) {
      if (typeof query.post_n === 'undefined') {
        this.$router.push('/');
      } else {
        this.contact.address.zip = query.post_n;
      }

      if (typeof query.amt_people === 'undefined') {
        this.setPeople(1)
      } else {
        this.setPeople(query.amt_people)
      }

      // Enhanced test mode activation
      if (typeof query.vzoman !== 'undefined' || this.shouldActivateTestMode()) {
        this.payment.type = 'Mobilepay'
      } 

      this.cart['luggageSmall'].amount = 0;
      this.cart['luggageLarge'].amount = 0;

    } else if (!this.$route.params.init) {
      this.$router.push('/')
    } else {
      // this.cart['luggageSmall'].amount = 0;
      // this.cart['luggageLarge'].amount = 0;

      this.cart['people'].forEach(function (person) {
        person.luggageSmall = 0;
        person.luggageLarge = 0;
      });
    }
    this.addHashToStep();
    this.queuePricingQuote(0);
  },
  beforeDestroy() {
    if (this.pricingDebounceTimer) {
      clearTimeout(this.pricingDebounceTimer);
      this.pricingDebounceTimer = null;
    }
  },
  methods: {
    ...mapMutations([
      'setCoupon',
      'setPeople',
      'setPricingLoading',
      'setPricingQuote',
      'setPricingError'
    ]),

    // Enhanced test mode detection
    shouldActivateTestMode() {
      const hasTestParam = this.$route.query.test_mode === 'enable_skip_payment';
      const hasTestStorage = localStorage.getItem('valizo_test_mode') === 'true';
      return hasTestParam || hasTestStorage;
    },

    buildPricingPayload() {
      return {
        pricingSessionId: this.pricingSessionId,
        puroadLevel: this.puroadLevel,
        body: {
          cart: this.cart,
          booking: this.bookingForSend && this.bookingForSend.date ? this.bookingForSend : this.booking,
          bookingHome: this.bookingHome,
          contact: this.contact,
          payment: this.payment
        }
      };
    },

    queuePricingQuote(delay = 250) {
      if (process.env.VUE_APP_PRICING_SOURCE === 'legacy') {
        return;
      }
      if (this.step === 5) {
        return;
      }
      if (this.pricingDebounceTimer) {
        clearTimeout(this.pricingDebounceTimer);
      }
      this.pricingDebounceTimer = setTimeout(() => {
        this.refreshPricingQuote();
      }, delay);
    },

    async refreshPricingQuote() {
      if (process.env.VUE_APP_PRICING_SOURCE === 'legacy') {
        return true;
      }

      this.pricingRequestCounter += 1;
      const requestId = `quote_${this.pricingRequestCounter}`;
      const requestSequence = this.pricingRequestCounter;
      this.setPricingLoading(true);
      this.setPricingError('');

      try {
        const payload = this.buildPricingPayload();
        const quote = await requestPricingQuote(payload);
        if (requestSequence !== this.pricingRequestCounter) {
          return true;
        }
        if (quote && quote.success) {
          if (process.env.VUE_APP_PRICING_SHADOW_MODE === 'true' && this.prices && typeof this.prices.total !== 'undefined') {
            const legacyTotal = Number(this.prices.total);
            const serverTotal = Number(quote.priceBreakdown ? quote.priceBreakdown.total : 0);
            if (legacyTotal !== serverTotal) {
              console.warn('[pricing-shadow-mismatch]', { legacyTotal, serverTotal });
            }
          }
          this.setPricingQuote({
            ...quote,
            requestId
          });
          return true;
        }

        this.setPricingError('quote_failed');
        return false;
      } catch (error) {
        this.setPricingError('quote_failed');
        return false;
      } finally {
        this.setPricingLoading(false);
      }
    },

    buildCheckoutPricingPayload() {
      return {
        pricingSessionId: this.pricingSessionId,
        quoteId: this.pricing.quoteId,
        quoteSignature: this.pricing.quoteSignature,
        inputHash: this.pricing.inputHash,
        puroadLevel: this.puroadLevel,
        body: {
          cart: this.cart,
          booking: this.bookingForSend && this.bookingForSend.date ? this.bookingForSend : this.booking,
          bookingHome: this.bookingHome,
          contact: this.contact,
          payment: this.payment
        }
      };
    },

    async ensureCheckoutPricing() {
      if (process.env.VUE_APP_PRICING_SOURCE === 'legacy') {
        return true;
      }

      if (!this.pricing.quoteId || !this.pricing.quoteSignature || !this.pricing.inputHash) {
        const refreshed = await this.refreshPricingQuote();
        if (!refreshed) {
          return false;
        }
      }

      try {
        const response = await validatePricingCheckout(this.buildCheckoutPricingPayload());
        if (response && response.success) {
          return true;
        }

        this.setPricingError('checkout_quote_invalid');
        return false;
      } catch (error) {
        const response = error && error.response ? error.response.data : null;
        if (response && response.reprice) {
          this.setPricingQuote({
            quoteId: this.pricing.quoteId,
            quoteSignature: this.pricing.quoteSignature,
            inputHash: response.inputHash || this.pricing.inputHash,
            expiresAt: this.pricing.expiresAt,
            currency: this.pricing.currency,
            rulesVersion: this.pricing.rulesVersion,
            priceBreakdown: response.reprice
          });
        }
        this.setPricingError('checkout_quote_invalid');
        return false;
      }
    },

    handleStepValid() {
      this.stepValid = true
    },

    changeBookingDateFormat(date) {
      if (date instanceof Date) {
        this.bookingForSend.date = ("0" + date.getDate()).slice(-2) + "-" + ("0"+(date.getMonth()+1)).slice(-2) + "-" + date.getFullYear();
      }
    },

    handleStepInvalid() {
      this.stepValid = false
    },

    prev() {
      if (this.step !== 1) {
        this.step--
      } else {
        this.$router.push('/')
      }
    },

    addHashToStep(){
      let hash = "step-" + this.step;
      
      if (this.$route.name === "book"){
        // Use Vue Router's replace to update hash without adding to history
        this.$router.replace({ 
          path: this.$route.path,
          query: this.$route.query,
          hash: '#' + hash 
        });
      }
    },

    async validateCoupon() {
      if (this.payment.coupon) {
        try {
          const coupon = await axios(process.env.VUE_APP_API_URL + '/coupon', {
            method: 'POST',
            data: {
              coupon: this.payment.coupon,
              email: this.contact.email,
              price: this.prices.total,
            }
          })

          if (coupon.status === 200) {
            this.setCoupon(coupon.data)
          }
        } catch (err) {
          //console.log(err)
        }
      }
    },

    async next() {
      if (this.waitingForResponse) {
        return;
      }

      if (this.step < 4) {
        this.step++
      } else if(this.step === 4) {
        this.bookingForSend = Object.assign({}, this.booking)
        this.changeBookingDateFormat(this.bookingForSend.date);
        await this.validateCoupon()
        const quoteUpdated = await this.refreshPricingQuote();
        if (!quoteUpdated) {
          alert('Pris kunne ikke opdateres. Prøv venligst igen.');
          return;
        }
        const isCheckoutPricingValid = await this.ensureCheckoutPricing();
        if (!isCheckoutPricingValid) {
          alert('Prisen blev opdateret. Bekræft venligst den nye pris før betaling.');
          return;
        }

        if (this.isFullCouponCheckout) {
          await this.order({
            paymentType: 'Rabatkode'
          })
          return;
        }

        if (this.payment.type !== 'Mobilepay' && this.payment.type !== 'Betal ved afhentning (MobilePay eller kontant)') {
          try {
            this.waitingForResponse = true;
            await this.startQuickpayRedirect();
          } catch (err) {
            alert('Betaling kunne ikke startes. Prøv venligst igen.');
          } finally {
            this.waitingForResponse = false;
          }
        } else {
          await this.order()
        }     
      } else {
        this.$router.push('/');
        this.step = 1;
      }
    },

    generateOrderRequestId() {
      return `req_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    },

    ensureOrderRequestId() {
      if (!this.orderRequestId) {
        this.orderRequestId = this.generateOrderRequestId();
      }
      return this.orderRequestId;
    },

    async startQuickpayRedirect() {
      const pricingOk = await this.ensureCheckoutPricing();
      if (!pricingOk) {
        throw new Error('Checkout pricing validation failed');
      }

      const amountMinor = Math.round(this.prices.total * 100);
      const apiBase = process.env.VUE_APP_API_URL || 'https://backend.valizo.eu/wp-json/api';

      const response = await axios.post(`${apiBase}/quickpay/init`, {
        amountMinor,
        currency: this.currency,
        request_id: this.ensureOrderRequestId(),
        referral: this.referral,
        puroad_level: this.puroadLevel.value || '',
        quoteId: this.pricing.quoteId,
        quoteSignature: this.pricing.quoteSignature,
        inputHash: this.pricing.inputHash,
        pricingSessionId: this.pricingSessionId,
        body: {
          cart: this.cart,
          booking: this.bookingForSend,
          bookingHome: this.bookingHome,
          contact: this.contact,
          payment: this.payment,
          price: this.totalPreCoupon
        }
      });

      const redirectUrl = response && response.data
        ? (response.data.redirect_url || response.data.redirectUrl)
        : null;
      const secureToken = response && response.data ? response.data.secure_token : null;

      if (secureToken) {
        try {
          sessionStorage.setItem('pending_secure_token', secureToken);
        } catch (e) {
          // ignore storage errors
        }
      }
      if (!redirectUrl) {
        throw new Error('Missing Quickpay redirect url');
      }

      window.location.href = redirectUrl;
    },

    async fetchSecureOrderWithRetry(apiBase, token, maxAttempts = 30, delayMs = 2000) {
      let lastError = null;
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        try {
          const response = await axios.get(`${apiBase}/secure-order/${token}`);
          if (response && response.data && response.data.success && response.data.data) {
            return response.data.data;
          }
        } catch (error) {
          lastError = error;
        }

        if (attempt < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, delayMs));
        }
      }
      if (lastError) throw lastError;
      throw new Error('Secure order not available yet');
    },

    async handleQuickpayReturn(status, token) {
      const apiBase = process.env.VUE_APP_API_URL || 'https://backend.valizo.eu/wp-json/api'
      const resolvedToken = token || sessionStorage.getItem('pending_secure_token');

      if (!resolvedToken) {
        console.error('[handleQuickpayReturn] No token available!');
        return;
      }

      if (status === 'cancelled') {
        alert('Betaling blev afbrudt. Prøv igen.');
        this.step = 4;
        return;
      }

      try {
        this.waitingForResponse = true;
        const orderData = await this.fetchSecureOrderWithRetry(apiBase, resolvedToken);
        this.restoreStateFromOrder(orderData);
        this.step = 5;
        try {
          sessionStorage.removeItem('pending_secure_token');
        } catch (e) {
          // ignore
        }
      } catch (e) {
        console.error('[handleQuickpayReturn] Error:', e);
      } finally {
        this.waitingForResponse = false;
      }
    },

    restoreStateFromOrder(order) {
      const paymentAmountRaw = order && order.payment && order.payment.amount !== undefined
        ? String(order.payment.amount).replace(',', '.')
        : null;
      const paymentAmount = paymentAmountRaw !== null ? Number(paymentAmountRaw) : NaN;
      this.payment.confirmedAmount = Number.isFinite(paymentAmount) ? paymentAmount : null;

      const hasDepartureService = typeof order.service_udrejse === 'boolean'
        ? order.service_udrejse
        : !!(order.booking && (order.booking.flight_number || order.booking.destination || order.booking.date));
      const hasReturnService = typeof order.service_hjemrejse === 'boolean'
        ? order.service_hjemrejse
        : !!order.include_home;

      this.cart.serviceUdrejse = hasDepartureService;
      this.cart.serviceHjemrejse = hasReturnService;

      // Contact
      this.contact.firstName = order.contact.first_name || '';
      this.contact.lastName = order.contact.last_name || '';
      this.contact.email = order.contact.email || '';
      if (order.contact.phone) {
        const parts = order.contact.phone.split(' ');
        this.contact.phone.prefix = parts.length > 1 ? parts[0] : '+45';
        this.contact.phone.number = parts.length > 1 ? parts.slice(1).join(' ') : parts[0];
      }
      this.contact.address.road = order.contact.address || '';
      this.contact.address.number = '';
      this.contact.address.zip = order.contact.zip || '';
      this.contact.address.city = order.contact.city || '';

      // Booking
      this.booking.destination = order.booking.destination || null;
      this.booking.airline = order.booking.airline || null;
      this.booking.flightNumber = order.booking.flight_number || null;
      this.booking.number = order.booking.booking_number || null;
      this.booking.date = order.booking.date || null;
      if (order.booking.time && order.booking.time.includes(':')) {
        const [hh, mm] = order.booking.time.split(':');
        this.booking.time = { HH: hh, mm: mm };
      }

      // Booking home
      this.cart.includeHome = !!order.include_home || hasReturnService;
      if (order.booking_home) {
        this.bookingHome.flightNumberHome = order.booking_home.flight_number || null;
        this.bookingHome.dateHome = order.booking_home.date || null;
        if (order.booking_home.time && order.booking_home.time.includes(':')) {
          const [hhH, mmH] = order.booking_home.time.split(':');
          this.bookingHome.timeHome = { HH: hhH, mm: mmH };
        }
        this.bookingHome.departureAirport = order.booking_home.departure_airport || null;
        this.bookingHome.transitStops = order.booking_home.transit_stops
          ? order.booking_home.transit_stops.split(',').map(s => s.trim()).filter(Boolean)
          : [];
        this.bookingHome.customsCode = order.booking_home.luggage_lock_code || '';
        this.bookingHome.commentHome = order.booking_home.comment || null;
        if (order.booking_home.travelers) {
          this.bookingHome.travelersUnder16 = parseInt(order.booking_home.travelers.under_16) || 0;
          this.bookingHome.travelersAge16 = parseInt(order.booking_home.travelers.age_16) || 0;
          this.bookingHome.travelersAge17 = parseInt(order.booking_home.travelers.age_17) || 0;
          this.bookingHome.travelersAge18Plus = parseInt(order.booking_home.travelers.age_18_plus) || 0;
        }
      }

      // People
      if (order.people && Array.isArray(order.people)) {
        this.cart.people = order.people.map((person, idx) => ({
          id: idx + 1,
          firstName: person.first_name || '',
          lastName: person.last_name || '',
          luggageSmall: parseInt(person.luggage_small) || 0,
          luggageLarge: parseInt(person.luggage_large) || 0
        }));
        this.cart.responsiblePersonId = order.responsible_person_id || 1;
        this.cart.luggageSmall.amount = this.cart.people.reduce((sum, person) => sum + (parseInt(person.luggageSmall, 10) || 0), 0);
        this.cart.luggageLarge.amount = this.cart.people.reduce((sum, person) => sum + (parseInt(person.luggageLarge, 10) || 0), 0);
      }

      // Luggage
      this.cart.luggageAirshell.amount = order.luggage && order.luggage.airshell ? parseInt(order.luggage.airshell) : 0;
      this.cart.luggagePlasticBag.amount = order.luggage && order.luggage.plasticbag ? parseInt(order.luggage.plasticbag) : 0;
      this.cart.luggageDescription = order.luggage && order.luggage.description ? order.luggage.description : '';

      // Payment
      this.payment.type = (order.payment && order.payment.type) || 'Betalingskort';
      this.payment.termsAccepted = true;
    },

    async order(options = {}) {
      if (this.waitingForResponse) {
        return;
      }

      const pricingOk = await this.ensureCheckoutPricing();
      if (!pricingOk) {
        alert('Prisen blev opdateret. Bekræft venligst den nye pris før betaling.');
        return;
      }

      this.payment.type = options.paymentType || "Betal ved afhentning (MobilePay eller kontant)";
      this.payment.confirmedAmount = Number(this.prices.total);
      try {
        this.waitingForResponse = true;
        await axios(`${process.env.VUE_APP_API_URL}/order`, {
          method: 'POST',
          data: {
            amount: this.prices.total,
            request_id: this.ensureOrderRequestId(),
            referral: this.referral,
            puroad_level: this.puroadLevel.value || '',
            quoteId: this.pricing.quoteId,
            quoteSignature: this.pricing.quoteSignature,
            inputHash: this.pricing.inputHash,
            pricingSessionId: this.pricingSessionId,
            body: {
              cart: this.cart,
              booking: this.bookingForSend,
              bookingHome: this.bookingHome,
              contact: this.contact,
              payment: this.payment,
              price: this.totalPreCoupon
            }
          }
        });
        this.goToConfirmationPage() // Go to a confirmation page

      } catch(err) {
        alert('Bestillingen kunne ikke oprettes. Prøv venligst igen.');
      } finally {
        this.waitingForResponse = false;
      }
    },

    goToConfirmationPage() {
      this.step = 5
    },
  },
}
</script>

<style lang="scss">
  @use "../assets/scss/variables" as *;
  @use "../assets/scss/media-queries" as *;

  .book {
    min-height: 100vh;
    justify-content: center;
    align-items: stretch;

    .content {
      width: calc(100% - 580px);
      border-left: 1px solid $grey-1;
      border-right: 1px solid $grey-1;
      border-bottom: 1px solid $grey-1;

      @include max-screen(1200px) {
        width: calc(100% - 340px);
      }

      @include max-screen(991px) {
        width: 100%;
      }
    }

    &--confirmation {
      .content {
        width: calc(100% - 240px);

        @include max-screen(1200px) {
          width: 100%;
        }
      }
    }

  }
</style>
